import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { PermissionDenied, RecordingLayer, RecordingSetup, RequestPermission } from '@/components/Teleprompter/Controls';
import { useDevice } from '@/components/Teleprompter/useDevice';
import { useRecording } from '@/components/Teleprompter/useRecording';
import Close from '@/images/Close.svg';
import { AvatarType } from '@/pages/avatar/common';
import { logger } from '@/services/logger';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

const CloseButton = styled(IconButton)`
    position: absolute;
    z-index: 5;
    top: 32px;
    right: 32px;
`;

const SideOverlay = styled.div`
    position: relative;
    width: 25%;
    height: 100%;
    background-color: #ef6c001a;
    backdrop-filter: blur(5px);
`;

const ControlsContainer = styled.div`
    position: relative;
    min-width: 408px;
    width: 50%;
    height: 100%;
    display: flex;
    justify-content: center;
    border-left: 4px dashed ${({ theme }) => theme.colors.warn[500]};
    border-right: 4px dashed ${({ theme }) => theme.colors.warn[500]};
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.25);
`;

const TeleprompterContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 2;

    display: flex;
    justify-content: center;
`;

const Controls = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    padding: 32px 0 52px;
    height: 100%;
`;

const VideoOverlay = styled.video`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: linear-gradient(0deg, rgba(10, 10, 10, 1) 0%, rgba(61, 61, 61, 1) 50%, rgba(61, 61, 61, 1) 100%);
    object-fit: cover;
`;

interface Props {
    text?: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    onChange: (video: Blob | null) => void;
    minRecordingTime?: number;
    recordingTime?: number;
    countdownTime?: number;
    wordsPerMinute?: number;
    avatarType?: AvatarType;
    nextPageStaticText?: string;
    lastSectionTime?: number;
    isStaticText?: boolean;
}

export const Teleprompter = ({
    text,
    open,
    setOpen,
    onChange,
    minRecordingTime = 15,
    recordingTime = 180,
    countdownTime = 5,
    wordsPerMinute,
    avatarType,
    nextPageStaticText,
    lastSectionTime,
    isStaticText,
}: Props) => {
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
    const [isRecordingTriggered, setIsRecordingTriggered] = useState<boolean>(false);
    const [safeZoneToggle, setSafeZoneToggle] = useState<boolean>(false);

    const {
        videoDevices,
        audioDevices,
        selectedVideoDevice,
        setSelectedVideoDevice,
        selectedAudioDevice,
        setSelectedAudioDevice,
        permissionStatus,
        requestDeviceAccess,
        disableDevice,
    } = useDevice();
    const { isRecording, startRecording, finishRecording, stopRecording, pauseRecording, resumeRecording } = useRecording(
        mediaStream,
        onChange
    );

    const videoRef = useRef<HTMLVideoElement>(null);

    const handleClear = () => {
        setIsRecordingTriggered(false);
        disableDevice();
        if (mediaStream) {
            mediaStream.getTracks().forEach(track => track.stop());
        }
    };

    useEffect(() => {
        const getMediaStream = async () => {
            if (!open) {
                return;
            }

            if (selectedVideoDevice && selectedAudioDevice) {
                try {
                    const constraints = {
                        video: {
                            deviceId: selectedVideoDevice,
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            frameRate: { ideal: 60 },
                        },
                        audio: {
                            deviceId: selectedAudioDevice,
                            echoCancellation: true,
                            noiseSuppression: true,
                            sampleRate: { ideal: 48000 },
                        },
                    };

                    const stream = await navigator.mediaDevices.getUserMedia(constraints);

                    setMediaStream(stream);
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                    }
                } catch (err) {
                    logger.error('Error accessing media stream:', err);
                }
            }
        };

        getMediaStream();
    }, [selectedVideoDevice, selectedAudioDevice, open]);

    useEffect(() => {
        return () => {
            handleClear();
        };
    }, []);

    if (!open) {
        document.body.style.overflow = 'auto';
        return null;
    }

    if (open) {
        window.scrollTo(0, 0);
        document.body.style.overflow = 'hidden';
    }

    const handleStopAndClose = () => {
        stopRecording();
        handleClose();
    };

    const handleClose = () => {
        setOpen(false);
        handleClear();
    };

    const handleFinish = async () => {
        await finishRecording();
        handleClose();
    };

    const handleTriggerRecording = () => {
        setIsRecordingTriggered(true);
    };

    const handleRestart = () => {
        stopRecording();
        setIsRecordingTriggered(false);
    };

    const handlePauseRecording = () => {
        pauseRecording();
    };

    const handleResumeRecording = () => {
        resumeRecording();
    };

    const controls = (
        <Controls>
            {permissionStatus === 'prompt' && <RequestPermission requestDeviceAccess={requestDeviceAccess} />}
            {permissionStatus === 'denied' && <PermissionDenied />}
            {!isRecordingTriggered && permissionStatus === 'granted' && (
                <RecordingSetup
                    selectedVideoDevice={selectedVideoDevice}
                    selectedAudioDevice={selectedAudioDevice}
                    videoDevices={videoDevices}
                    audioDevices={audioDevices}
                    setSelectedVideoDevice={setSelectedVideoDevice}
                    setSelectedAudioDevice={setSelectedAudioDevice}
                    handleTriggerRecording={handleTriggerRecording}
                    avatarType={avatarType}
                    safeZoneToggle={safeZoneToggle}
                    setSafeZoneToggle={setSafeZoneToggle}
                />
            )}

            {isRecordingTriggered && (
                <RecordingLayer
                    isRecording={isRecording}
                    text={text}
                    startRecording={startRecording}
                    finishRecording={handleFinish}
                    restartRecording={handleRestart}
                    minRecordingTime={minRecordingTime}
                    recordingTime={recordingTime}
                    countdownTime={countdownTime}
                    wordsPerMinute={wordsPerMinute}
                    safeZoneToggle={safeZoneToggle}
                    pauseRecording={handlePauseRecording}
                    resumeRecording={handleResumeRecording}
                    nextPageStaticText={nextPageStaticText}
                    lastSectionTime={lastSectionTime}
                    isStaticText={isStaticText}
                />
            )}
        </Controls>
    );

    return (
        <TeleprompterContainer>
            <CloseButton ariaLabel="close" onClick={handleStopAndClose}>
                <MaskedIcon src={Close} color="white" size="32px" />
            </CloseButton>

            <VideoOverlay ref={videoRef} autoPlay playsInline muted />

            {avatarType === AvatarType.PremiumPlus && safeZoneToggle ? (
                <>
                    <SideOverlay />
                    <ControlsContainer>{controls}</ControlsContainer>
                    <SideOverlay />
                </>
            ) : (
                controls
            )}
        </TeleprompterContainer>
    );
};
